// PE-0000 — Classify portal questions and answer from existing portal APIs
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PatientChatChoice, PatientChatIntent, PatientChatReply } from '../models/patient-chat.model';
import { isBookAppointmentEnabled } from '../utils/book-appointment-settings.util';
// PE-6332 — reuse the same self-scheduled check the appointments page uses for Cancel/Reschedule
import { isPatientBookedAppointment, readEnrichedStringUid } from '../utils/reschedule-cancel-policy.util';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { AuthStateService } from './auth-state.service';
import { DateUtilityService } from './date-utility.service';
import { PatientChatBookingService } from './patient-chat-booking.service';
import { StorageService } from './storage.service';

const MAX_QUESTION_LENGTH = 500;
const INBOX_COMMUNICATION_TYPE = 0;
const LOOKBACK_YEARS = 1;
const MAX_APPOINTMENT_ACTION_CHOICES = 8;
type AppointmentAction = 'cancel' | 'reschedule';

const FALLBACK_REPLY: PatientChatReply = {
  text: 'I can help with appointments, messages, forms, records, and statements. For anything else, use Messages or call the office.',
  route: '/messages',
  routeLabel: 'Open messages',
};

const CLINICAL_REPLY: PatientChatReply = {
  text: 'I cannot answer medical questions. Your care team can. Open Ask Provider, or open Results to view what is already on file.',
  route: '/ask-provider',
  routeLabel: 'Ask provider',
};

export interface PatientChatTranscriptLine {
  id: number;
  from: 'patient' | 'assistant';
  text: string;
  route?: string;
  routeLabel?: string;
}

export interface PatientChatTranscript {
  open: boolean;
  lines: PatientChatTranscriptLine[];
  choices: PatientChatChoice[];
  draft: string;
  showCaptcha: boolean;
  nextId: number;
}

const CHAT_GREETING = 'Hello. I can help you with appointments, messages, forms, health records, and statements.';

function emptyTranscript(open = false): PatientChatTranscript {
  return {
    open,
    lines: [{ id: 1, from: 'assistant', text: CHAT_GREETING }],
    choices: [],
    draft: '',
    showCaptcha: false,
    nextId: 2,
  };
}

interface PatientContext {
  patientId: string;
  formsPatientId: string;
  tenantId: string;
  prid: string;
  bookingEnabled: boolean;
}

// PE-0000 — keyword routing. Clinical questions are matched before page names.
export function classifyPatientQuestion(raw: string): PatientChatIntent {
  const text = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!text) {
    return 'unknown';
  }
  if (isClinicalQuestion(text)) {
    return 'clinical';
  }
  if (isGreeting(text)) {
    return 'greeting';
  }
  // PE-6332 — cancel / reschedule must win before the generic "appointment" and "book" checks
  if (includesAny(text, ['cancel']) && includesAny(text, ['appointment', 'visit'])) {
    return 'cancel-appointment';
  }
  if (
    includesAny(text, ['reschedule']) ||
    (includesAny(text, ['move', 'change']) && includesAny(text, ['appointment', 'visit']))
  ) {
    return 'reschedule-appointment';
  }
  if (includesAny(text, ['book', 'schedule a', 'schedule an', 'make an appointment', 'new appointment'])) {
    return 'book-appointment';
  }
  if (includesAny(text, ['next appointment', 'upcoming', 'when is my', 'next visit'])) {
    return 'next-appointment';
  }
  if (includesAny(text, ['appointment'])) {
    return 'open-appointments';
  }
  if (includesAny(text, ['ask provider', 'ask my provider', 'message my provider', 'contact my provider'])) {
    return 'ask-provider';
  }
  if (includesAny(text, ['message', 'inbox'])) {
    return 'messages';
  }
  if (includesAny(text, ['form'])) {
    return 'forms';
  }
  if (includesAny(text, ['statement', 'balance', 'bill', 'amount due'])) {
    return 'statements';
  }
  if (includesAny(text, ['password', 'account setting'])) {
    return 'account-settings';
  }
  if (includesAny(text, ['profile'])) {
    return 'profile';
  }
  if (includesAny(text, ['lab', 'result'])) {
    return 'results';
  }
  if (includesAny(text, ['visit summary', 'visit note'])) {
    return 'visit-summary';
  }
  if (includesAny(text, ['exam note', 'exam notes'])) {
    return 'exam-notes';
  }
  if (includesAny(text, ['health information', 'health record'])) {
    return 'health-information';
  }
  if (includesAny(text, ['help', 'what can you'])) {
    return 'help';
  }
  return 'unknown';
}

@Injectable({ providedIn: 'root' })
export class PatientChatService {
  private readonly apiService = inject(ApiService);
  private readonly authStateService = inject(AuthStateService);
  private readonly authService = inject(AuthService);
  private readonly storageService = inject(StorageService);
  private readonly dateUtility = inject(DateUtilityService);
  private readonly booking = inject(PatientChatBookingService);
  // PE-6332 — thread survives closing the panel; only Start over clears it
  private transcript: PatientChatTranscript = emptyTranscript();

  readTranscript(): PatientChatTranscript {
    return {
      ...this.transcript,
      lines: this.transcript.lines.map((line) => ({ ...line })),
      choices: this.transcript.choices.map((choice) => ({ ...choice })),
    };
  }

  rememberTranscript(transcript: PatientChatTranscript): void {
    this.transcript = {
      ...transcript,
      lines: transcript.lines.map((line) => ({ ...line })),
      choices: transcript.choices.map((choice) => ({ ...choice })),
    };
  }

  clearTranscript(): void {
    const open = this.transcript.open;
    this.booking.reset();
    this.transcript = emptyTranscript(open);
  }

  ask(question: string): Observable<PatientChatReply> {
    const trimmed = question.trim().slice(0, MAX_QUESTION_LENGTH);
    if (this.booking.isActive()) {
      if (this.shouldLeaveBooking(trimmed)) {
        this.booking.reset();
      } else if (this.shouldRestartBooking(trimmed)) {
        // PE-6332 — keep the date, visit type, and location written in this sentence
        return this.booking.start(trimmed);
      } else {
        return this.booking.continueWithText(trimmed);
      }
    }
    const intent = classifyPatientQuestion(trimmed);
    switch (intent) {
      case 'next-appointment':
        return this.loadNextAppointment();
      // PE-6332 — open the specific appointment on the appointments page, same rules as a manual click
      case 'cancel-appointment':
        return this.loadAppointmentForAction('cancel');
      case 'reschedule-appointment':
        return this.loadAppointmentForAction('reschedule');
      case 'messages':
        return this.loadMessageCount();
      case 'forms':
        return this.loadFormCount();
      case 'statements':
        return this.loadStatementSummary();
      case 'book-appointment':
        if (!this.readContext().bookingEnabled) {
          return of(this.bookReply());
        }
        return this.booking.start(trimmed);
      default:
        return of(staticReply(intent));
    }
  }

  // PE-0000 / PE-6332 — button selection during booking, or picking which appointment to act on
  selectChoice(choiceId: string): Observable<PatientChatReply> {
    const appointmentAction = parseAppointmentActionChoiceId(choiceId);
    if (appointmentAction) {
      return of(this.appointmentActionRoute(appointmentAction.action, appointmentAction.apptId));
    }
    return this.booking.selectChoice(choiceId);
  }

  // PE-0000 — captcha passed; save the selected slot
  confirmBooking(token: string): Observable<PatientChatReply> {
    return this.booking.confirmBooking(token);
  }

  private shouldLeaveBooking(question: string): boolean {
    if (this.booking.matchesCurrentChoice(question)) {
      return false;
    }
    const intent = classifyPatientQuestion(question);
    return intent !== 'unknown' && intent !== 'book-appointment';
  }

  // PE-6332 — Start over in the chat header drops any booking in progress
  resetBooking(): void {
    this.booking.reset();
  }

  // PE-6332 — a new book sentence reloads booking and keeps details from that sentence
  private shouldRestartBooking(question: string): boolean {
    return classifyPatientQuestion(question) === 'book-appointment'
      && !this.booking.matchesCurrentChoice(question);
  }

  private bookReply(): PatientChatReply {
    return {
      text: 'Appointment booking is not available on this account. I can open your appointments instead.',
      route: '/appointments',
      routeLabel: 'Open appointments',
    };
  }

  private loadNextAppointment(): Observable<PatientChatReply> {
    const context = this.readContext();
    const pageReply: PatientChatReply = {
      text: 'I can open your appointments.',
      route: '/appointments',
      routeLabel: 'Open appointments',
    };
    if (!context.patientId || !context.tenantId) {
      return of(pageReply);
    }
    return this.apiService
      .getPatientAppointments(context.patientId, context.tenantId, true, context.prid)
      .pipe(
        map((response) => summarizeNextAppointment(response)),
        catchError((error: unknown) => this.failOpen('next-appointment', error, pageReply))
      );
  }

  // PE-6332 — find the appointment(s) the patient can cancel/reschedule online and open the
  // right one on the appointments page, where the existing cutoff and policy rules apply.
  private loadAppointmentForAction(action: AppointmentAction): Observable<PatientChatReply> {
    const context = this.readContext();
    const verb = action === 'cancel' ? 'cancel' : 'reschedule';
    const pageReply: PatientChatReply = {
      text: `I can open your appointments so you can ${verb} one.`,
      route: '/appointments',
      routeLabel: 'Open appointments',
    };
    if (!context.patientId || !context.tenantId) {
      return of(pageReply);
    }
    return this.apiService
      .getPatientAppointments(context.patientId, context.tenantId, true, context.prid)
      .pipe(
        map((response) => this.buildAppointmentActionReply(action, response)),
        catchError((error: unknown) =>
          this.failOpen(action === 'cancel' ? 'cancel-appointment' : 'reschedule-appointment', error, pageReply)
        )
      );
  }

  private buildAppointmentActionReply(action: AppointmentAction, response: unknown): PatientChatReply {
    const verb = action === 'cancel' ? 'cancel' : 'reschedule';
    // PE-6332 — only appointments booked through the portal can be cancelled/rescheduled online,
    // the same gate the appointments page applies before it shows those buttons
    const eligible = readSuccessfulRows(response)
      .filter((row) => isPatientBookedAppointment(row))
      .map((row) => ({ row, when: readAppointmentDate(row) }))
      .filter((item): item is { row: Record<string, unknown>; when: Date } => item.when !== null)
      .sort((left, right) => left.when.getTime() - right.when.getTime());

    if (eligible.length === 0) {
      return {
        text: `I do not see an appointment booked online that I can ${verb} here. You can review the full list on the appointments page.`,
        route: '/appointments',
        routeLabel: 'Open appointments',
      };
    }

    if (eligible.length === 1) {
      const apptId = readAppointmentActionId(eligible[0].row);
      if (!apptId) {
        return {
          text: `I can open your appointments so you can ${verb} one.`,
          route: '/appointments',
          routeLabel: 'Open appointments',
        };
      }
      return this.appointmentActionRoute(action, apptId, this.appointmentChoiceLabel(eligible[0].row, eligible[0].when));
    }

    const choices: PatientChatChoice[] = eligible
      .slice(0, MAX_APPOINTMENT_ACTION_CHOICES)
      .map((item) => ({ id: `${action}-appt:${readAppointmentActionId(item.row)}`, label: this.appointmentChoiceLabel(item.row, item.when) }))
      .filter((choice) => choice.id !== `${action}-appt:`);

    return {
      text: `Which appointment do you want to ${verb}?`,
      choices,
    };
  }

  private appointmentActionRoute(action: AppointmentAction, apptId: string, detailLabel?: string): PatientChatReply {
    const verb = action === 'cancel' ? 'cancel' : 'reschedule';
    const policy = action === 'cancel' ? 'cancellation' : 'rescheduling';
    const subject = detailLabel ? `your ${detailLabel} appointment` : 'that appointment';
    return {
      text: `Opening ${subject} to ${verb}. The practice's ${policy} rules apply there.`,
      route: `/appointments?chatAction=${action}&apptId=${encodeURIComponent(apptId)}`,
      routeLabel: action === 'cancel' ? 'Open to cancel' : 'Open to reschedule',
    };
  }

  private appointmentChoiceLabel(row: Record<string, unknown>, when: Date): string {
    const dateLabel = when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const visitType = readLabel(row, 'SCHAppointmentTypeName');
    return [visitType, dateLabel].filter((part) => part.length > 0).join(' · ');
  }

  private loadMessageCount(): Observable<PatientChatReply> {
    const context = this.readContext();
    const pageReply: PatientChatReply = {
      text: 'I can open your messages.',
      route: '/messages',
      routeLabel: 'Open messages',
    };
    if (!context.patientId || !context.tenantId) {
      return of(pageReply);
    }
    const range = this.pastYearRange();
    return this.apiService
      .getMessages(
        context.patientId,
        INBOX_COMMUNICATION_TYPE,
        range.from,
        range.to,
        context.tenantId,
        context.prid
      )
      .pipe(
        map((response) => summarizeMessages(response)),
        catchError((error: unknown) => this.failOpen('messages', error, pageReply))
      );
  }

  private loadFormCount(): Observable<PatientChatReply> {
    const context = this.readContext();
    const pageReply: PatientChatReply = {
      text: 'I can open your forms.',
      route: '/patient-forms',
      routeLabel: 'Open forms',
    };
    if (!context.formsPatientId || !context.tenantId) {
      return of(pageReply);
    }
    return this.apiService.getAllPortalForms(context.tenantId, context.formsPatientId).pipe(
      map((response) => summarizeForms(response)),
      catchError((error: unknown) => this.failOpen('forms', error, pageReply))
    );
  }

  private loadStatementSummary(): Observable<PatientChatReply> {
    const context = this.readContext();
    const pageReply: PatientChatReply = {
      text: 'I can open your statements.',
      route: '/statements',
      routeLabel: 'Open statements',
    };
    if (!context.patientId || !context.tenantId) {
      return of(pageReply);
    }
    const range = this.pastYearRange();
    return this.apiService
      .getStatements(context.patientId, context.tenantId, range.from, range.to)
      .pipe(
        map((response) => summarizeStatements(response)),
        catchError((error: unknown) => this.failOpen('statements', error, pageReply))
      );
  }

  private pastYearRange(): { from: string; to: string } {
    const today = this.dateUtility.getCurrentDate();
    const start = new Date();
    start.setFullYear(start.getFullYear() - LOOKBACK_YEARS);
    const month = String(start.getMonth() + 1).padStart(2, '0');
    const day = String(start.getDate()).padStart(2, '0');
    const fromIso = `${start.getFullYear()}-${month}-${day}`;
    return {
      from: this.dateUtility.formatDateForApi(fromIso),
      to: this.dateUtility.formatDateForApi(today),
    };
  }

  private readContext(): PatientContext {
    const userInfo = this.authStateService.userInfo;
    const patientId = readPatientId(userInfo);
    const tenantId = readStorageString(this.storageService.getSessionStorage('tenant_id'));
    return {
      patientId,
      formsPatientId: patientId.includes('-') ? patientId.split('-')[0] : patientId,
      tenantId,
      prid: this.authService.getPrid(),
      bookingEnabled: isBookAppointmentEnabled(this.storageService.getSessionStorage('user_permissions')),
    };
  }

  private failOpen(intent: PatientChatIntent, error: unknown, reply: PatientChatReply): Observable<PatientChatReply> {
    const status = readHttpStatus(error);
    console.error('PE-0000 patient chat request failed', intent, status);
    return of({
      text: 'I could not load that just now. You can open the page instead.',
      route: reply.route,
      routeLabel: reply.routeLabel,
    });
  }
}

function staticReply(intent: PatientChatIntent): PatientChatReply {
  switch (intent) {
    case 'greeting':
    case 'help':
      return {
        text: 'Hello. I can help you with appointments, messages, forms, health records, and statements. Choose a topic or ask in your own words.',
      };
    case 'open-appointments':
      return {
        text: 'I can open your appointments.',
        route: '/appointments',
        routeLabel: 'Open appointments',
      };
    case 'ask-provider':
      return {
        text: 'I can open Ask Provider. You send the message from that page.',
        route: '/ask-provider',
        routeLabel: 'Ask provider',
      };
    case 'results':
      return {
        text: 'I can open your lab results. I do not interpret them.',
        route: '/health-record/results',
        routeLabel: 'Open results',
      };
    case 'visit-summary':
      return {
        text: 'I can open your visit summaries.',
        route: '/health-record/visit-summary',
        routeLabel: 'Open visit summaries',
      };
    case 'exam-notes':
      return {
        text: 'I can open your exam notes.',
        route: '/health-record/exam-notes',
        routeLabel: 'Open exam notes',
      };
    case 'health-information':
      return {
        text: 'I can open your health information.',
        route: '/health-record/health-information',
        routeLabel: 'Open health information',
      };
    case 'profile':
      return {
        text: 'I can open your profile.',
        route: '/profile',
        routeLabel: 'Open profile',
      };
    case 'account-settings':
      return {
        text: 'You can change your password in Account settings.',
        route: '/account-settings',
        routeLabel: 'Open account settings',
      };
    case 'clinical':
      return CLINICAL_REPLY;
    default:
      return FALLBACK_REPLY;
  }
}

function summarizeNextAppointment(response: unknown): PatientChatReply {
  const rows = readSuccessfulRows(response);
  const upcoming = rows
    .map((row) => ({ row, when: readAppointmentDate(row) }))
    .filter((item) => item.when !== null)
    .sort((left, right) => (left.when as Date).getTime() - (right.when as Date).getTime());
  if (upcoming.length === 0) {
    return {
      text: 'I do not see an upcoming appointment. You can review the full list on the appointments page.',
      route: '/appointments',
      routeLabel: 'Open appointments',
    };
  }
  const next = upcoming[0].row;
  const when = upcoming[0].when as Date;
  const dateLabel = when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeLabel = formatAppointmentTime(next['SCHStartTime']);
  const provider = readLabel(next, 'PVFullProviderName');
  const visitType = readLabel(next, 'SCHAppointmentTypeName');
  const location = readLabel(next, 'SCHLocationName');
  const details = [dateLabel, timeLabel, provider, visitType, location].filter((part) => part.length > 0);
  return {
    text: `Your next appointment is ${details.join(', ')}.`,
    route: '/appointments',
    routeLabel: 'Open appointments',
  };
}

function summarizeMessages(response: unknown): PatientChatReply {
  const rows = readMessageRows(response).filter((row) => {
    return Boolean(row['CommDetailsID'] || row['Subject'] || row['nPatientPortalAccessID']);
  });
  const count = rows.length;
  const noun = count === 1 ? 'message' : 'messages';
  return {
    text: `You have ${count} inbox ${noun} from the past year.`,
    route: '/messages',
    routeLabel: 'Open messages',
  };
}

function summarizeForms(response: unknown): PatientChatReply {
  const rows = Array.isArray(response) ? asRecords(response) : readSuccessfulRows(response);
  const active = rows.filter((row) => row['bisActive'] === true);
  const count = active.length;
  const noun = count === 1 ? 'form' : 'forms';
  return {
    text: `You have ${count} active ${noun}. Open the forms page to complete one. I will not submit a form for you.`,
    route: '/patient-forms',
    routeLabel: 'Open forms',
  };
}

function summarizeStatements(response: unknown): PatientChatReply {
  const rows = readSuccessfulRows(response).filter(isStatementRow);
  if (rows.length === 0) {
    return {
      text: 'I do not see a statement for the past year. You can review statements on that page.',
      route: '/statements',
      routeLabel: 'Open statements',
    };
  }
  const latest = [...rows].sort((left, right) => statementTime(right) - statementTime(left))[0];
  const amount = formatUsd(latest['AmountToPay'] ?? latest['amount']);
  const countLabel = rows.length === 1 ? '1 statement' : `${rows.length} statements`;
  const amountLabel = amount ? ` The latest amount due is ${amount}.` : '';
  return {
    text: `You have ${countLabel} from the past year.${amountLabel}`,
    route: '/statements',
    routeLabel: 'Open statements',
  };
}

function isClinicalQuestion(text: string): boolean {
  return (
    /\b(symptom|symptoms|medicine|medication|dosage|diagnosis|diagnose|treatment|pain|sick)\b/.test(text) ||
    /side effect/.test(text) ||
    /should i take/.test(text) ||
    /what does (this|my|the) (result|lab)/.test(text)
  );
}

function isGreeting(text: string): boolean {
  return /^(hi|hello|hey|thanks|thank you|good morning|good afternoon)[.! ]*$/.test(text);
}

function includesAny(text: string, phrases: string[]): boolean {
  return phrases.some((phrase) => text.includes(phrase));
}

function readSuccessfulRows(response: unknown): Record<string, unknown>[] {
  if (!response || typeof response !== 'object') {
    return [];
  }
  const body = response as { success?: unknown; data?: unknown };
  if (body.success !== undefined && body.success !== 'true' && body.success !== true) {
    return [];
  }
  return unwrapRows(body.data);
}

function readMessageRows(response: unknown): Record<string, unknown>[] {
  if (!response || typeof response !== 'object') {
    return [];
  }
  return unwrapRows((response as { data?: unknown }).data);
}

function unwrapRows(data: unknown): Record<string, unknown>[] {
  if (!Array.isArray(data)) {
    return [];
  }
  if (data.length === 1 && Array.isArray(data[0])) {
    return asRecords(data[0]);
  }
  return asRecords(data);
}

function asRecords(value: unknown[]): Record<string, unknown>[] {
  return value.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item));
}

function readAppointmentDate(row: Record<string, unknown>): Date | null {
  const raw = row['SCHStartDate'];
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return null;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatAppointmentTime(value: unknown): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 0) {
    return '';
  }
  const padded = digits.padStart(4, '0');
  const hours = Number(padded.slice(0, 2));
  const minutes = padded.slice(2, 4);
  if (!Number.isFinite(hours)) {
    return '';
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHours}:${minutes} ${period}`;
}

function readLabel(row: Record<string, unknown>, key: string): string {
  const value = row[key];
  return typeof value === 'string' ? value.trim() : '';
}

// PE-6332 — same id the appointments page reads from this row (SCHAppointmentUID) so the
// chosen appointment matches exactly once the page re-reads it from the URL.
function readAppointmentActionId(row: Record<string, unknown>): string {
  return readEnrichedStringUid(row, 'SCHAppointmentUID', 'schAppointmentUID', 'SCHAppointmentUid', 'schAppointmentUid');
}

function parseAppointmentActionChoiceId(choiceId: string): { action: AppointmentAction; apptId: string } | null {
  const match = /^(cancel|reschedule)-appt:(.+)$/.exec(choiceId);
  if (!match) {
    return null;
  }
  return { action: match[1] as AppointmentAction, apptId: match[2] };
}

function isStatementRow(row: Record<string, unknown>): boolean {
  const keys = Object.keys(row);
  if (keys.length === 0 || (keys.length === 1 && keys[0] === '')) {
    return false;
  }
  return Boolean(
    row['PatientAccountNumber'] ||
      row['accountNumber'] ||
      row['PatientStatementDate'] ||
      row['statementDate'] ||
      row['PatientStatementID'] ||
      row['statementId'] ||
      row['nTempleteTransactionID']
  );
}

function statementTime(row: Record<string, unknown>): number {
  const raw = row['PatientStatementDate'] ?? row['statementDate'];
  if (typeof raw !== 'string') {
    return 0;
  }
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatUsd(value: unknown): string | null {
  const amount = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  if (!Number.isFinite(amount)) {
    return null;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function readPatientId(userInfo: unknown): string {
  if (!userInfo || typeof userInfo !== 'object') {
    return '';
  }
  const patientId = (userInfo as { patientId?: unknown }).patientId;
  return patientId == null ? '' : String(patientId).trim();
}

function readStorageString(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function readHttpStatus(error: unknown): string {
  if (error && typeof error === 'object' && 'status' in error) {
    return String((error as { status: unknown }).status);
  }
  return 'unknown';
}
